import * as z from 'zod';

import { FeeId, FeeLineItemId, PaymentDueId } from '../BrandedIds';
import { zodJSInstantDate, zodJSPlainDate } from '../dates/BrandedDate';
import { AdvanceCategory, AdvanceType, FeeLineItemInput, FeeStatus } from '../events/CreateChargeEvent';
import { InvestorRemittanceSplit } from './InvestorRemittanceSplit';

export const FeeLineItem = FeeLineItemInput.extend({
  id: FeeLineItemId,
});

export type FeeLineItem = z.infer<typeof FeeLineItem>;

export const AdvanceConfig = z.object({
  advanceType: AdvanceType,
  advanceCategory: AdvanceCategory.optional(),
  advanceCategoryDetail: z.string().optional(),
  interestRate: z.number(),
});

export type AdvanceConfig = z.infer<typeof AdvanceConfig>;

export const ChargeBase = z.object({
  id: FeeId,
  type: z.literal('fee').default('fee'),
  totalAmount: z.number(),
  amountRemaining: z.number(),
  status: FeeStatus,
  chargeDate: zodJSPlainDate,
  updatedAt: zodJSInstantDate.optional(),
  cancelledAt: zodJSInstantDate.optional(), // TODO backfill this for already cancelled charges
  comment: z.string().optional(),
  investorRemittanceSplit: InvestorRemittanceSplit.optional(),
  isDeferred: z.boolean(),
  createdDate: zodJSInstantDate.optional(),
});

export const Advance = ChargeBase.extend({
  type: z.literal('advance'),
  advanceConfig: AdvanceConfig.optional(),
  recipient: z.string().optional(),
  advanceInterest: z.number(), // amount remaining
  advancePrincipal: z.number(), // amount remaining

  // amounts charged
  originalPrincipal: z.number(),
  advanceInterestCharged: z.number(),
  advanceInterestUpdatedAt: zodJSInstantDate.optional(),
});

export type Advance = z.infer<typeof Advance>;

export const Fee = ChargeBase.extend({
  type: z.literal('fee'),
  lineItems: z.array(FeeLineItem),
  paymentDueId: PaymentDueId.optional(), // used to track automated fees
});

export type Fee = z.infer<typeof Fee>;

export const Charge = z.discriminatedUnion('type', [Advance, Fee]);
export type Charge = z.infer<typeof Charge>;

export function isFee(item: Charge): item is Fee {
  return item.type === 'fee';
}

export function isAdvance(item: Charge): item is Advance {
  return item.type === 'advance';
}
